import { supabase } from './supabase';

export interface HrProfile {
  id: string;
  user_id: string | null;
  national_id: string;
  phone: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  gender: string | null;
  marital_status: string | null;
  city: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  bank_name: string | null;
  bank_branch: string | null;
  bank_account_number: string | null;
  created_at: string;
  updated_at: string;
}

export interface HrEmployment {
  id: string;
  company_id: string;
  profile_id: string;
  status: 'pending_approval' | 'active' | 'terminated';
  job_title: string | null;
  start_date: string | null;
  end_date: string | null;
  invited_at: string;
  approved_at: string | null;
  terminated_at: string | null;
  invited_by: string | null;
  invite_token: string;
  created_at: string;
}

export interface HrEmploymentWithCompany extends HrEmployment {
  company_name: string;
}

export interface HrEmploymentWithProfile extends HrEmployment {
  hr_profiles: HrProfile;
}

export interface InviteTokenResult {
  employment_id: string;
  company_id: string;
  company_name: string;
  profile_id: string;
  national_id: string;
  phone: string;
  status: string;
  job_title: string | null;
}

export interface EmployeeSessionRow {
  profile_id: string;
  national_id: string;
  phone: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  gender: string | null;
  marital_status: string | null;
  city: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  bank_name: string | null;
  bank_branch: string | null;
  bank_account_number: string | null;
  employment_id: string | null;
  employment_company_id: string | null;
  company_name: string | null;
  employment_status: string | null;
  job_title: string | null;
  start_date: string | null;
  end_date: string | null;
  invited_at: string | null;
  approved_at: string | null;
}

type ProfileUpdatable = Partial<Pick<HrProfile,
  'first_name' | 'last_name' | 'email' | 'phone' |
  'date_of_birth' | 'gender' | 'marital_status' |
  'city' | 'street' | 'house_number' | 'postal_code' |
  'bank_name' | 'bank_branch' | 'bank_account_number'
>>;

export const hrProfileService = {
  async getMyProfile(): Promise<{ data: HrProfile | null; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: null, error: 'לא מחובר' };
      const { data, error } = await supabase
        .from('hr_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) return { data: null, error: error.message };
      return { data: data as HrProfile | null, error: null };
    } catch (e) {
      return { data: null, error: (e as Error).message };
    }
  },

  async updateMyProfile(updates: ProfileUpdatable): Promise<{ data: HrProfile | null; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: null, error: 'לא מחובר' };
      const { data, error } = await supabase
        .from('hr_profiles')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .maybeSingle();
      if (error) return { data: null, error: error.message };
      return { data: data as HrProfile | null, error: null };
    } catch (e) {
      return { data: null, error: (e as Error).message };
    }
  },

  async getEmployeeSession(): Promise<{ data: EmployeeSessionRow[]; error: string | null }> {
    try {
      const { data, error } = await supabase.rpc('get_employee_session');
      if (error) return { data: [], error: error.message };
      return { data: (data ?? []) as EmployeeSessionRow[], error: null };
    } catch (e) {
      return { data: [], error: (e as Error).message };
    }
  },

  async resolveInviteToken(token: string): Promise<{ data: InviteTokenResult | null; error: string | null }> {
    try {
      const { data, error } = await supabase.rpc('resolve_hr_invite_token', { p_token: token });
      if (error) return { data: null, error: error.message };
      const rows = data as InviteTokenResult[];
      return { data: rows?.[0] ?? null, error: null };
    } catch (e) {
      return { data: null, error: (e as Error).message };
    }
  },

  async linkProfileToUser(profileId: string, userId: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase
        .from('hr_profiles')
        .update({ user_id: userId })
        .eq('id', profileId);
      if (error) return { error: error.message };
      return { error: null };
    } catch (e) {
      return { error: (e as Error).message };
    }
  },

  async approveEmployment(employmentId: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase
        .from('hr_employments')
        .update({ status: 'active', approved_at: new Date().toISOString() })
        .eq('id', employmentId);
      if (error) return { error: error.message };
      return { error: null };
    } catch (e) {
      return { error: (e as Error).message };
    }
  },

  async getMyEmployments(): Promise<{ data: HrEmployment[]; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: [], error: 'לא מחובר' };
      const { data: profile } = await supabase
        .from('hr_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!profile) return { data: [], error: null };
      const { data, error } = await supabase
        .from('hr_employments')
        .select('*')
        .eq('profile_id', profile.id)
        .order('invited_at', { ascending: false });
      if (error) return { data: [], error: error.message };
      return { data: (data ?? []) as HrEmployment[], error: null };
    } catch (e) {
      return { data: [], error: (e as Error).message };
    }
  },

  // --- Company-side methods ---

  async createInvitation(params: {
    companyId: string;
    nationalId: string;
    phone: string;
    jobTitle?: string;
  }): Promise<{ data: { profileId: string; employmentId: string; inviteToken: string } | null; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: null, error: 'לא מחובר' };

      let profileId: string;
      const { data: existing } = await supabase
        .from('hr_profiles')
        .select('id')
        .eq('national_id', params.nationalId)
        .maybeSingle();

      if (existing) {
        profileId = existing.id;
      } else {
        const { data: newProfile, error: profileError } = await supabase
          .from('hr_profiles')
          .insert({ national_id: params.nationalId, phone: params.phone })
          .select('id')
          .single();
        if (profileError || !newProfile) return { data: null, error: profileError?.message ?? 'שגיאה ביצירת פרופיל' };
        profileId = newProfile.id;
      }

      const { data: employment, error: empError } = await supabase
        .from('hr_employments')
        .insert({
          company_id: params.companyId,
          profile_id: profileId,
          job_title: params.jobTitle ?? null,
          invited_by: user.id,
          status: 'pending_approval',
        })
        .select('id, invite_token')
        .single();
      if (empError || !employment) return { data: null, error: empError?.message ?? 'שגיאה ביצירת הזמנה' };

      await supabase
        .from('hr_employment_onboarding')
        .insert({ employment_id: employment.id })
        .single();

      return {
        data: {
          profileId,
          employmentId: employment.id,
          inviteToken: employment.invite_token,
        },
        error: null,
      };
    } catch (e) {
      return { data: null, error: (e as Error).message };
    }
  },

  async listCompanyEmployments(companyId: string): Promise<{ data: HrEmploymentWithProfile[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('hr_employments')
        .select('*, hr_profiles(*)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) return { data: [], error: error.message };
      return { data: (data ?? []) as HrEmploymentWithProfile[], error: null };
    } catch (e) {
      return { data: [], error: (e as Error).message };
    }
  },
};
